import { CheckCircle2 } from 'lucide-react';
import { useEffect, useSyncExternalStore } from 'react';

import {
  dismissStatusToast,
  getStatusToast,
  subscribeStatusToast,
} from '../../../app/status-toast-store';

/** How long a status toast stays on screen before dismissing itself. */
export const STATUS_TOAST_DURATION_MS = 5000;

/**
 * Host for the PWA status toast (QA R2-15): an accessible confirmation
 * ("Cash payment recorded · #3") for actions whose dialog closes silently.
 * QA R3: it was easy to miss at the bottom in small text, so it now sits at
 * the top just below the sticky header (h-16 + safe area), stays 5 s, and uses
 * a larger icon/text in the success color.
 *
 * The live region is rendered ALWAYS (empty when idle) — screen readers only
 * announce changes inside a region that already exists. Mounted once in the
 * root route; trigger it with `showStatusToast(message)`.
 */
export function StatusToast() {
  const toast = useSyncExternalStore(subscribeStatusToast, getStatusToast, getStatusToast);
  const toastId = toast?.id;

  useEffect(() => {
    if (toastId === undefined) return;
    const timer = setTimeout(dismissStatusToast, STATUS_TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [toastId]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-4 z-50 mx-auto max-w-sm"
      style={{ top: 'calc(4rem + env(safe-area-inset-top) + 0.75rem)' }}
    >
      {toast ? (
        <div className="pointer-events-auto flex items-center gap-3 rounded-xl border border-success/50 bg-card px-4 py-4 shadow-lg ring-1 ring-success/30">
          <CheckCircle2 className="size-7 shrink-0 text-success" aria-hidden />
          <p className="text-base font-semibold text-foreground">{toast.message}</p>
        </div>
      ) : null}
    </div>
  );
}
